import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { v4 } from "uuid";
import { useSnackbar } from "../../core/contexts/SnackbarContext";
import { MetodoPagamento } from "../../core/enum/MetodoPagamento";
import { TipoAnexoPedido } from "../enum/TipoAnexoPedido";
import { TipoPedido } from "../enum/TipoPedido";
import { useFetchPedidos } from "../hooks/useFetchPedidos";
import { usePedidos } from "../hooks/usePedidos";
import { IItemProduto } from "../interface/IItemProduto";
import { IPedido } from "../interface/IPedido";
import { IProdutoPedido } from "../interface/IProdutoPedido";
import { IConfiguracaoPedidos } from "../interface/IConfiguracaoPedidos";
import { useConfiguracoesPedidoContext } from "./ConfiguracoesPedidoContext";
import { TipoProduto } from "../../produtos/enum/TipoProduto";
import { StatusPedido, StatusPedidoDescricao } from "../enum/StatusPedido";
import { IEndereco } from "../../core/interfaces/IEndereco";
import { ICliente } from "../../clientes/interface/ICliente";
import moment from "moment";
import { format } from "path";
import { TipoItemProduto } from "../../produtos/enum/TipoItemProduto";
import { EmitirNotaFiscalModal, TipoPessoa } from "../pages/Operacao/containers/EmitirNotaFiscalModal";
import { useHotkeys } from "react-hotkeys-hook";
import { isModoTecladoAtivo } from "../../core/hooks/useModoTeclado";
import { IDadosEmitirNotaFiscal } from "../interface/IDadosEmitirNotaFiscal";
import { validarCpf } from "../../core/utils/validarCpf";
import { validarCnpj } from "../../core/utils/validarCnpj";
import { IFluxoContexto } from "../interface/IFluxoContexto";
import { IConfiguracaoComanda } from "../interface/IConfiguracaoComanda";
import { TipoGatilhoComanda } from "../enum/TipoGatilhoComanda";
import { IProduto } from "../../produtos/interfaces/IProduto";
import { TipoMarketplace } from "../../marketplace/enum/TipoMarketplace";
import { IPagamentoPedido } from "../interface/IPagamentoPedido";
import { useEntregas } from "../hooks/useEntregas";
import { TipoCobrancaEntrega } from "../enum/TipoCobrancaEntrega";
import { useAutenticacaoContext } from "../../core/contexts/AutenticacaoContext";
import { IComplementoItensPedido } from "../interface/IComplementoItensPedido";
import { useProdutos } from "../../produtos/hooks/useProdutos";
import { DividirPagamento } from "../pages/Operacao/containers/DividirPagamento";
import { DividirPedido } from "../pages/Operacao/containers/DividirPedido";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useWebSocketPedidosContext } from "./WebSocketPedidosContext";
import { ICupom } from "../interface/ICupom";
import { calcularValoresComCupons } from "../utils/calcularValoresComCupons";
import { useCuponsPedido } from "../hooks/useCuponsPedido";
import { filtrarCuponsProdutosRemovidos } from "../utils/verificarCupomAssociadoProduto";

interface IOperacaoContextProps {
    agendadoPara: moment.Moment | null;
    agendarHorario: (hora: moment.Moment | null) => void;
    configuracao: IConfiguracaoPedidos | null | undefined;
    cliente: ICliente | undefined;
    produtos: IProdutoPedido[];
    valorTotalProdutos: number;
    valorTotalPedido: number;
    valorDescontoTotal: number;
    valorTaxaServico: number;
    percentualTaxaServico: number;
    taxaServicoAplicavel: boolean;
    taxaServicoRemovida: boolean;
    alternarTaxaServico: () => void;
    pagamentos: IPagamentoPedido[];
    alterarPagamentos: (pagamentos?: IPagamentoPedido[] | undefined) => void;
    adicionarProduto: (produto: IProdutoPedido) => void;
    adicionarProdutosCombo: (produtos: IProdutoPedido[]) => void;
    editarProduto: (produto: IProdutoPedido) => void;
    aumentarQuantidade: (produtoUuid: string) => Promise<void>;
    diminuirQuantidade: (produtoUuid: string) => Promise<void>;
    removerProduto: (produtoUuid: string) => Promise<void>;
    abrirPedido: (dadosPedido?: any) => Promise<IPedido>;
    fecharPedido: (dados?: IDadosEmitirNotaFiscal, pagamentos?: IPagamentoPedido[]) => Promise<void>
    loading: boolean;
    tipo: TipoPedido;
    alterarTipo: (tipo: TipoPedido) => void;
    pedidos: IPedido[];
    valorTroco: number | undefined;
    valorRecebido: number | undefined;
    valorFrete: number;
    alterarValorFrete: (valor: number) => void;
    alterarEndereco: () => void;
    removerCliente: () => void;
    vincularEndereco: (endereco: IEndereco) => void;
    endereco: IEndereco | undefined;
    vincularCliente: (cliente: ICliente) => void;
    salvarPedido: () => void;
    resetarTudo: () => void;
    numeroTotalItens: number;
    numeroPedidoDiario?: number;
    switchNotaFiscal: boolean;
    atualizarMesasSilenciosamente: () => Promise<void>;
    switchImprimirAutomatico: boolean;
    handleSwitchImprimirAutomatico: () => void;
    modalNotaFiscal: boolean;
    handleModalNotaFiscal: () => void;
    handleSwitchNotaFiscal: () => void;
    selecionarProduto: (produto: IProdutoPedido) => void;
    produtosSelecionados: IProdutoPedido[];
    fluxoContexto?: IFluxoContexto;
    configuracaoNFCe?: IConfiguracaoComanda;
    loadingVincularEndereco: boolean;
    drawerPagamentoAberto: boolean;
    abrirDrawerPagamento: () => void;
    fecharDrawerPagamento: () => void;
    cupons: ICupom[];
    aplicarCupom: (cupom: ICupom) => Promise<void>;
    removerCupom: (codigo: string, tipo: string) => Promise<void>;
    drawerCuponsAberto: boolean;
    abrirDrawerCupons: () => void;
    fecharDrawerCupons: () => void;
}

export const useOperacaoContext = () => {
    return useContext(OperacaoContext)
}
export const OperacaoContext = createContext({} as IOperacaoContextProps)

export type TipoDivisaoPagamento = "Quantidade de pessoas" | "Produtos selecionados" | "Valores selecionados";

export const OperacaoProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const snackbar = useSnackbar();
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const { configuracao } = useConfiguracoesPedidoContext();
    const { registrarCallbackAtualizacao, removerCallbackAtualizacao } = useWebSocketPedidosContext();
    const tipoParam = searchParams.get('tipo') as TipoPedido | null;
    const tipo = tipoParam ?? (configuracao?.fluxoOperacao?.fluxosContextos?.[0]?.tipo as TipoPedido) ?? TipoPedido.delivery;

    const [pagamentos, setPagamentos] = useState<IPagamentoPedido[]>([]);
    const [produtos, setProdutos] = useState<IProdutoPedido[]>([]);
    const [endereco, setEndereco] = useState<IEndereco | undefined>(undefined);
    const [cliente, setCliente] = useState<ICliente | undefined>(undefined);
    const [agendadoPara, setAgendadoPara] = useState<moment.Moment | null>(null);
    const [numeroPedidoDiario, setNumeroPedidoDiario] = useState<undefined | number>(undefined);
    const [switchNotaFiscal, setSwitchNotaFiscal] = useState(false);
    const [switchImprimirAutomatico, setSwitchImprimirAutomatico] = useState(true);
    const [modalNotaFiscal, setModalNotaFiscal] = useState(false);
    const [produtosSelecionados, setProdutosSelecionados] = useState<IProdutoPedido[]>([]);
    const [loadingVincularEndereco, setLoadingVincularEndereco] = useState(false);
    const { tratarProduto } = useProdutos();
    const [drawerPagamentoAberto, setDrawerPagamento] = useState(false);
    const [cupons, setCupons] = useState<ICupom[]>([]);
    const [drawerCuponsAberto, setDrawerCupons] = useState(false);
    const [taxaServicoRemovida, setTaxaServicoRemovida] = useState(false);
    const { aplicarCupom: aplicarCupomAPI, removerCupom: removerCupomAPI } = useCuponsPedido();
    
    const abrirDrawerPagamento = () => {
        setDrawerPagamento(true);
    }

    const fecharDrawerPagamento = () => {
        setDrawerPagamento(false);
    }

    const abrirDrawerCupons = () => {
        setDrawerCupons(true);
    };

    const fecharDrawerCupons = () => {
        setDrawerCupons(false);
    };

    const [drawerDividirAberto, setDrawerDividir] = useState(false);

    const abrirDrawerDividir = () => {
        setDrawerDividir(true);
    }

    const fecharDrawerDividir = () => {
        setDrawerDividir(false);
    }

    useHotkeys(['f8'], () => {
        window.dispatchEvent(new CustomEvent('limparFiltroProdutos'));
        window.dispatchEvent(new CustomEvent('resetarModoTeclado'));
        fecharPedido();
    }, {
        enabled: true // Sempre habilitado, o useModoTeclado vai tratar
    }, [
        tipo,
        pagamentos,
        produtos,
        configuracao,
        endereco,
        cliente,
        agendadoPara,
        numeroPedidoDiario,
        switchNotaFiscal,
        modalNotaFiscal,
    ]);

    useHotkeys(['f9'], () => {
        window.dispatchEvent(new CustomEvent('limparFiltroProdutos'));
        window.dispatchEvent(new CustomEvent('resetarModoTeclado'));
        salvarPedido();
    }, {
        enabled: true // Sempre habilitado, o useModoTeclado vai tratar
    }, [
        tipo,
        pagamentos,
        produtos,
        configuracao,
        endereco,
        cliente,
        agendadoPara,
        numeroPedidoDiario,
        switchNotaFiscal,
        modalNotaFiscal,
    ]);

    const handleModalNotaFiscal = () => {
        setModalNotaFiscal(!modalNotaFiscal);
    }

    const handleSwitchNotaFiscal = () => {
        setSwitchNotaFiscal(!switchNotaFiscal);
    }

    const handleSwitchImprimirAutomatico = () => {
        setSwitchImprimirAutomatico(!switchImprimirAutomatico);
    }

    const valorFrete = useMemo(
        () => (endereco?.regiao?.preco ?? 0),
        [endereco]
    );

    const urlFetch = useMemo(() => {
        if (tipo == TipoPedido.mesa) {
            return `?status=Em andamento&tipos=${TipoPedido.mesa}`;
        }

        if (tipo == TipoPedido.delivery) {
            return `?status=Em andamento&tipos=${TipoPedido.delivery}`;
        }

        if (tipo == TipoPedido.retirada) {
            return `?status=Em andamento&tipos=${TipoPedido.retirada}`;
        }

        if (tipo == TipoPedido.simples) {
            return `?status=Em andamento&tipos=${TipoPedido.simples}`;
        }
    }, [tipo]);

    const { pedidos, mutate, handleRefresh } = useFetchPedidos(urlFetch);

    const { estabelecimento } = useAutenticacaoContext();

    const {
        gerarPedido,
        loading,
        atualizarStatus,
        adicionarProduto: adicionarProdutoPedido,
        removerProduto: removerProdutoPedido,
        gerarAnexo: gerarAnexoPedido,
        atualizarPagamentos: atualizarPagamentosPedido,
        alterarEndereco: atualizarEndereco,
        emitirNotaFiscal: emitirNotaFiscalConsumidor,
        dividirPedido: dividirPedidoHook
    } = usePedidos();

    const { calcularFrete, loading: loadingEntregas } = useEntregas();

    const alterarTipo = (novoTipo: TipoPedido) => {
        setSearchParams(prev => {
            const newParams = new URLSearchParams(prev);
            newParams.set('tipo', novoTipo);
            return newParams;
        });
        resetarTudo();
    };
    
    const resetarProdutos = () => {
        setProdutos([]);
    }

    const resetarCheckout = () => {
        setAgendadoPara(null);
        setPagamentos([]);
        setCliente(undefined);
        setEndereco(undefined);
        setNumeroPedidoDiario(undefined);
    }

    const resetarTudo = () => {
        resetarProdutos();
        resetarCheckout();
        setCupons([]);
        setTaxaServicoRemovida(false);
    }

    const aplicarCupom = async (cupom: ICupom) => {
        const sucesso = await aplicarCupomAPI(undefined, cupom);
        if (sucesso) {
            setCupons((prev) => {
                // Verificar se o cupom já foi aplicado
                // Para cupons de fidelidade, verificar por programaFidelidadeUuid e nome
                // Para outros cupons, verificar por código e tipo
                const jaExiste = prev.some((c) => {
                    // Se é um cupom de fidelidade (tem programaFidelidadeUuid)
                    if (cupom.programaFidelidadeUuid && c.programaFidelidadeUuid) {
                        // Verificar se é do mesmo programa
                        if (c.programaFidelidadeUuid === cupom.programaFidelidadeUuid) {
                            // Se é o cupom principal (nome "Itens Fidelidade" ou "Cashback Fidelidade")
                            if (cupom.nome === "Itens Fidelidade" || cupom.nome === "Cashback Fidelidade") {
                                // Verificar se já existe um cupom principal do mesmo programa
                                return c.nome === cupom.nome;
                            }
                            // Para cupons de itens individuais (nome começa com "Item fidelidade")
                            if (cupom.nome && cupom.nome.startsWith("Item fidelidade")) {
                                // Só verificar duplicidade se o cupom existente também for individual do mesmo programa
                                if (c.nome && c.nome.startsWith("Item fidelidade")) {
                                    // Verificar se já existe um cupom individual com o mesmo UUID do produto no código
                                    if (cupom.codigo && c.codigo && cupom.codigo.includes('-') && c.codigo.includes('-')) {
                                        // Extrair o UUID do produto do código (última parte após o último hífen)
                                        const partesCupom = cupom.codigo.split('-');
                                        const partesExistente = c.codigo.split('-');
                                        // Se ambos têm o mesmo UUID do produto (última parte)
                                        if (partesCupom.length > 0 && partesExistente.length > 0) {
                                            const uuidProdutoCupom = partesCupom[partesCupom.length - 1];
                                            const uuidProdutoExistente = partesExistente[partesExistente.length - 1];
                                            if (uuidProdutoCupom === uuidProdutoExistente && uuidProdutoCupom.length > 10) {
                                                return true;
                                            }
                                        }
                                    }
                                }
                                // Se chegou aqui, não é duplicado (é um novo item ou comparado com cupom não-individual)
                                return false;
                            }
                            // Se é cupom de fidelidade mas não é principal nem individual, não é duplicado
                            return false;
                        }
                        // Se são de programas diferentes, não são duplicados
                        return false;
                    }
                    // Verificação padrão por código e tipo (apenas para cupons não-fidelidade)
                    if (!cupom.programaFidelidadeUuid && !c.programaFidelidadeUuid) {
                        return c.codigo === cupom.codigo && c.tipo === cupom.tipo;
                    }
                    // Se um é fidelidade e outro não, não são duplicados
                    return false;
                });
                if (jaExiste) {
                    snackbar("Este cupom já foi aplicado", { severity: "warning" });
                    return prev;
                }
                // Sempre adicionar o cupom se não for duplicado
                return [...prev, cupom];
            });
        } else {
            // Se aplicarCupomAPI retornou false, ainda assim tentar adicionar localmente para cupons de fidelidade
            // (pois no fluxo tradicional não há pedidoUuid, então sempre retorna true)
            if (cupom.programaFidelidadeUuid) {
                setCupons((prev) => {
                    const jaExiste = prev.some((c) => {
                        if (cupom.programaFidelidadeUuid && c.programaFidelidadeUuid) {
                            if (c.programaFidelidadeUuid === cupom.programaFidelidadeUuid) {
                                if (cupom.nome === "Itens Fidelidade" || cupom.nome === "Cashback Fidelidade") {
                                    return c.nome === cupom.nome;
                                }
                                if (cupom.nome && cupom.nome.startsWith("Item fidelidade") && c.nome && c.nome.startsWith("Item fidelidade")) {
                                    if (cupom.codigo && c.codigo && cupom.codigo.includes('-') && c.codigo.includes('-')) {
                                        const partesCupom = cupom.codigo.split('-');
                                        const partesExistente = c.codigo.split('-');
                                        if (partesCupom.length > 0 && partesExistente.length > 0) {
                                            const uuidProdutoCupom = partesCupom[partesCupom.length - 1];
                                            const uuidProdutoExistente = partesExistente[partesExistente.length - 1];
                                            if (uuidProdutoCupom === uuidProdutoExistente && uuidProdutoCupom.length > 10) {
                                                return true;
                                            }
                                        }
                                    }
                                }
                            }
                        }
                        return false;
                    });
                    if (!jaExiste) {
                        return [...prev, cupom];
                    }
                    return prev;
                });
            }
        }
    };

    const removerCupom = async (codigo: string, tipo: string) => {
        const sucesso = await removerCupomAPI(undefined, codigo, tipo as any);
        if (sucesso) {
            setCupons((prev) => {
                return prev.filter((c) => !(c.codigo === codigo && c.tipo === tipo));
            });
        }
    };

    const editarProduto = async (produto: IProdutoPedido) => {
        await removerProduto(produto.uuid);
        await adicionarProduto(produto);
    }

    const valorTotalProdutos = useMemo(() => {
        let totalProdutos = 0;

        produtos.map(produto => {
            let totalProduto = (produto.preco ?? 0);

            produto.itens?.map(item => {
                totalProduto = totalProduto + ((item.preco ?? 0) * (item.quantidade ?? 1));
            })

            totalProdutos = totalProdutos + (totalProduto * (produto.quantidade ?? 1));
        })

        return totalProdutos;
    }, [produtos]);

    const valoresCalculados = useMemo(() => {
        const valores = calcularValoresComCupons(
            {
                valorTotalProdutos: valorTotalProdutos,
                valorFrete: valorFrete,
            },
            cupons
        );
        return valores;
    }, [valorTotalProdutos, valorFrete, cupons]);

    const taxaServicoAplicavel = useMemo(() => {
        const config = configuracao?.taxaServico;
        return !!config && (config.contextos?.includes(tipo) ?? false);
    }, [configuracao, tipo]);

    const percentualTaxaServico = useMemo(() => {
        if (!taxaServicoAplicavel || taxaServicoRemovida) {
            return 0;
        }
        return configuracao?.taxaServico?.percentual ?? 0;
    }, [taxaServicoAplicavel, taxaServicoRemovida, configuracao]);

    const valorTaxaServico = useMemo(() => {
        const base = valoresCalculados.valorTotalProdutos + valoresCalculados.valorFrete;
        return (base * percentualTaxaServico) / 100;
    }, [valoresCalculados, percentualTaxaServico]);

    const alternarTaxaServico = () => setTaxaServicoRemovida(prev => !prev);

    const valorTotalPedido = useMemo(() => {
        return valoresCalculados.valorTotalPedido + valorTaxaServico;
    }, [valoresCalculados, valorTaxaServico]);

    const valorDescontoTotal = useMemo(() => {
        return valoresCalculados.valorDescontoTotal;
    }, [valoresCalculados]);

    const numeroTotalItens = useMemo(() => {
        let total = 0;

        produtos.map(produto => {
            total += produto.quantidade ?? 1
        })

        return total
    }, [produtos, valorTotalPedido])

    const valorRecebido = useMemo(() => {
        let total = 0;

        const pagamentosDinheiro = pagamentos?.filter(pagamento => pagamento.metodoPagamento == MetodoPagamento.Dinheiro) ?? [];
        pagamentosDinheiro?.map(pagamento => {
            total = total + (pagamento.valorRecebido ?? 0);
        })

        return total;
    }, [pagamentos]);
    const valorTroco = useMemo(() => valorRecebido ? (valorRecebido - valorTotalPedido) : 0, [valorTotalPedido, valorRecebido])

    const fluxoContexto = useMemo(() => {
        return configuracao?.fluxoOperacao?.fluxosContextos?.find(fluxo => fluxo.tipo === tipo);
    }, [configuracao, tipo])

    const configuracaoNFCe = useMemo(() => {
        return fluxoContexto?.configuracoesComandas.find(config => config.tipo == TipoAnexoPedido.nota_fiscal);
    }, [fluxoContexto])


    useEffect(() => {
        if (configuracaoNFCe?.obrigatorio == true) {
            setSwitchNotaFiscal(true);
        } else {
            setSwitchNotaFiscal(false);
        }
    }, [fluxoContexto, configuracaoNFCe])

    const alterarPagamentos = async (pagamentos?: IPagamentoPedido[]) => {
        if (!pagamentos) {
            pagamentos = [];
        }

        setPagamentos(pagamentos);
    }

    const adicionarProdutosCombo = (novosProdutos: IProdutoPedido[]) => {
        if (pagamentos.length) {
            alterarPagamentos([]);
        }
        setProdutos(prev => [...prev, ...novosProdutos]);
    };

    const adicionarProduto = async (produto: IProdutoPedido): Promise<IProdutoPedido | undefined> => {
        if (pagamentos.length) {
            await alterarPagamentos([]);
            snackbar("O pagamento foi removido", { severity: "info" })
        }
        
        // Não precisa remover cupons ao adicionar produto, apenas reaplicar se necessário
        // Os cupons serão reaplicados automaticamente quando o pedido for salvo/fechado
        
        const produtoJaExiste = produtos.find(produtoCarrinho =>
            produtoCarrinho.uuidProdutoReferencia === produto.uuidProdutoReferencia &&
            !produtoCarrinho.comboUuid
        );

        if (
            produtoJaExiste &&
            produtoJaExiste.uuid &&
            produto.tipo == TipoProduto.industrializado
        ) {
            await aumentarQuantidade(produtoJaExiste.uuid);
            // Retornar o produto existente (a quantidade já foi aumentada no estado)
            return produtoJaExiste;
        }

        if (
            produtoJaExiste &&
            produtoJaExiste.uuid &&
            produto.tipo == TipoProduto.preparado &&
            (
                produto.configuracao?.habilitarComplementos == false ||
                produto.configuracao?.habilitarObservacao == false
            )
        ) {
            await aumentarQuantidade(produtoJaExiste.uuid);
            // Retornar o produto existente (a quantidade já foi aumentada no estado)
            return produtoJaExiste;
        }

        const produtoTratado = tratarProduto(produto);
        if (!produtoTratado) {
            snackbar("Produto invalido", { severity: "error" });
            return undefined;
        }
        setProdutos((produtosPedido) => [...produtosPedido, produtoTratado]);

        return produtoTratado;
    }

    const salvarPedido = async () => {
        const dados = {
            status: StatusPedidoDescricao["Em andamento"]
        } as IPedido
        const pedido = await abrirPedido(dados, true);
        if (pedido) {
            snackbar("Pedido salvo com sucesso", { severity: "success" })
        }
    }

    const limparPedido = () => {
        setProdutosSelecionados([]);
        setProdutos([]);
        setPagamentos([]);
        setCliente(undefined);
        setEndereco(undefined);
        setNumeroPedidoDiario(undefined);
        setAgendadoPara(null);
        setCupons([]);
        setTaxaServicoRemovida(false);
    }

    const abrirPedido = async (dadosPedido?: IPedido, limpar?: boolean) => {
        const produtosPedido = dadosPedido?.produtos ? dadosPedido?.produtos?.map(produto => {
            return tratarProduto(produto);
        }) : produtos;

        const dadosTratadosPedido = {
            tipo: dadosPedido?.tipo ?? tipo,
            produtos: produtosPedido,
            pagamentos: pagamentos,
            mesaId: dadosPedido?.mesaId,
            cliente: dadosPedido?.cliente ?? cliente,
            endereco: dadosPedido?.endereco ?? endereco,
            agendadoPara: dadosPedido?.agendadoPara ?? agendadoPara?.format("YYYY-MM-DDTHH:mm"),
            status: dadosPedido?.status,
            cupons: cupons.length > 0 ? cupons : undefined,
            percentualTaxaServico: taxaServicoRemovida ? 0 : undefined,
            marketplace: {
                tipo: TipoMarketplace.PDV
            }
        } as IPedido;

        const { pedido } = await gerarPedido(dadosTratadosPedido, tipo);

        if (pedido) {
            handleRefresh()
            if (!limpar) {
                navigate(`/pedidos/operacao/edicao/${pedido.uuid}?redirect=/pedidos/operacao?tipo=mesa`);
            } else {
                setTimeout(() => limparPedido(), 1000)
            }
        }

        return pedido;
    }

    const fecharPedido = async (dadosEmitirNota?: IDadosEmitirNotaFiscal, pagamentosParametro?: IPagamentoPedido[]) => {
        if (switchNotaFiscal && !modalNotaFiscal) {
            return handleModalNotaFiscal();
        }

        if (dadosEmitirNota && switchNotaFiscal) {
            if (dadosEmitirNota.tipoPessoa == "CPF" && !validarCpf(dadosEmitirNota.numeroPessoa, true)) {
                return snackbar("CPF inválido", { severity: "error" });
            }

            if (dadosEmitirNota.tipoPessoa == "CNPJ" && !validarCnpj(dadosEmitirNota.numeroPessoa, true)) {
                return snackbar("CNPJ inválido", { severity: "error" });
            }
        }

        const fluxo = fluxoContexto?.fluxo;
        if (!produtos.length) {
            return snackbar("Nenhum produto adicionado ao pedido", { severity: "error" })
        }
        const dadosPedido = {
            agendadoPara: agendadoPara?.format("YYYY-MM-DDTHH:mm"),
            produtos: produtos,
            endereco,
            cliente,
            tipo,
            status: fluxo == "PDV" ? "Concluído" : "Em preparo",
            pagamentos: pagamentosParametro ?? pagamentos,
            cupons: cupons.length > 0 ? cupons : undefined,
            percentualTaxaServico: taxaServicoRemovida ? 0 : undefined,
            marketplace: {
                tipo: TipoMarketplace.PDV
            }
        } as IPedido;
        const resultado = await gerarPedido(dadosPedido, tipo);
        if (!resultado) {
            snackbar("Ocorreu um erro ao fechar o pedido", { severity: "error" })
            return;
        }
        if (switchImprimirAutomatico) {
            gerarAnexosBotaoFecharPedido(resultado.pedido, dadosEmitirNota);
        }
        snackbar("Pedido fechado com sucesso", { severity: "success" });
        setProdutos([]);
        setPagamentos([]);
        setCliente(undefined);
        setNumeroPedidoDiario(undefined);
        setEndereco(undefined);
        setAgendadoPara(null);
        setCupons([]);
    }

    const gerarAnexosBotaoFecharPedido = async (pedido: IPedido, dados?: IDadosEmitirNotaFiscal) => {
        if (switchNotaFiscal == true) {
            setTimeout(async () => await emitirNotaFiscalConsumidor(pedido.uuid, dados), 1000);
            handleModalNotaFiscal();
        }

        const configuracoesComanda = fluxoContexto?.configuracoesComandas.filter(config => {
            if (config.gatilhos.includes(TipoGatilhoComanda.BOTAO_CONCLUIR) && config.obrigatorio && config.imprimirAutomaticamente !== false) {
                return config;
            }
        });

        const configuracoesNaoFiscais = configuracoesComanda?.filter(config => config.tipo != TipoAnexoPedido.nota_fiscal);
        configuracoesNaoFiscais?.forEach((config, index) => {
            setTimeout(async () => {
                await gerarAnexoPedido(pedido.uuid, config.tipo, config.uuid);
            }, 1000 * (index + 1));
        });
    }

    const aumentarQuantidade = async (uuid: string) => {
        if (pagamentos.length) {
            await alterarPagamentos([]);
            snackbar("O pagamento foi removido", { severity: "info" })
        }

        // Não precisa remover cupons ao aumentar quantidade, apenas reaplicar se necessário

        if (!produtos || !produtos.length) {
            return snackbar("Nenhum produto no carrinho", { severity: "error" });
        }

        const index = produtos.findIndex(produto => produto.uuid === uuid);

        if (index === -1) {
            return snackbar("Produto não encontrado", { severity: "error" });
        }

        produtos[index].quantidade = (produtos[index].quantidade ?? 0) + 1;
        setProdutos([...produtos]);
    };

    const diminuirQuantidade = async (uuid: string) => {
        if (pagamentos.length) {
            await alterarPagamentos([]);
            snackbar("O pagamento foi removido", { severity: "info" })
        }

        if (!produtos || !produtos.length) {
            return snackbar("Nenhum produto no carrinho", { severity: "error" });
        }

        const index = produtos.findIndex(produto => produto.uuid === uuid);

        if (index === -1) {
            return snackbar("Produto não encontrado", { severity: "error" });
        }

        const produtoAtual = produtos[index];
        const novaQuantidade = (produtoAtual.quantidade ?? 0) - 1;

        if (!novaQuantidade) {
            // Se a quantidade chegou a zero, o produto será removido
            // Verificar e remover cupons associados a esse produto antes de remover
            const cuponsParaRemover = filtrarCuponsProdutosRemovidos(cupons, [produtoAtual]);
            if (cuponsParaRemover.length > 0) {
                for (const cupom of cuponsParaRemover) {
                    await removerCupom(cupom.codigo || "", cupom.tipo);
                }
            }
            // Remover o produto do array
            produtos.splice(index, 1);
            setProdutos([...produtos]);
        } else {
            produtos[index].quantidade = novaQuantidade;
            setProdutos([...produtos]);
        }
    };

    const removerProduto = async (uuid: string) => {
        if (pagamentos.length) {
            await alterarPagamentos([]);
            snackbar("O pagamento foi removido", { severity: "info" })
        }

        const index = produtos.findIndex(produto => produto.uuid === uuid);

        if (index === -1) {
            return snackbar("Não foi possível remover o produto", { severity: "error" });
        }

        const produtoRemovido = produtos[index];
        const quantidade = !produtoRemovido.quantidade ? 1 : produtoRemovido.quantidade;

        // Verificar e remover apenas cupons associados ao produto removido
        const cuponsParaRemover = filtrarCuponsProdutosRemovidos(cupons, [produtoRemovido]);
        if (cuponsParaRemover.length > 0) {
            for (const cupom of cuponsParaRemover) {
                await removerCupom(cupom.codigo || "", cupom.tipo);
            }
            if (cuponsParaRemover.length === 1) {
                snackbar("Cupom removido devido à remoção do produto", { severity: "info" });
            } else {
                snackbar(`${cuponsParaRemover.length} cupons removidos devido à remoção do produto`, { severity: "info" });
            }
        }

        produtos.splice(index, 1);
        const produtosAtualizados = [...produtos];
        setProdutos(produtosAtualizados);
    }

    const agendarHorario = (hora: moment.Moment | null) => {
        if (hora) {
            setAgendadoPara(hora);
        }
    }

    const alterarEndereco = () => {
        // Reaplicar cupons quando endereço mudar (não remover, apenas reaplicar)
        // Os cupons serão reaplicados automaticamente quando o pedido for salvo/fechado
        setEndereco(undefined);
    }

    const removerCliente = () => {
        setEndereco(undefined);
        setCliente(undefined);
    }

    const vincularCliente = (cliente: ICliente) => {
        setCliente(cliente);
    }

    const vincularEndereco = async (props: IEndereco) => {
        let regiao = props?.regiao;

        if (configuracao?.tipoCobrancaEntrega == TipoCobrancaEntrega.DISTANCIA) {
            if (!configuracao?.distanciasEntrega?.length) {
                return snackbar("Nenhuma distância configurada para calcular o frete", { severity: 'error' });
            }

            if (!estabelecimento?.endereco) {
                return snackbar("O endereço do estabelecimento não está configurado", { severity: 'error' });
            }

            setLoadingVincularEndereco(true);

            const regiaoGerada = await calcularFrete({
                enderecoDestino: props,
                distancias: configuracao?.distanciasEntrega,
                enderecoOrigem: estabelecimento?.endereco
            });

            setLoadingVincularEndereco(false);

            if (!regiaoGerada) {
                return;
            }

            regiao = regiaoGerada;
        }

        if (configuracao?.tipoCobrancaEntrega == TipoCobrancaEntrega.REGIAO) {
            if (!regiao) {
                return snackbar("Região não configurada", { severity: "error" });
            }
            const regiaoAtualizada = configuracao?.regioesEntrega?.find(r => r.uuid === regiao!.uuid);
            if (regiaoAtualizada) {
                regiao = { ...regiao, preco: regiaoAtualizada.preco };
            }
        }

        const endereco = {
            ...props,
            regiao
        }
        // Reaplicar cupons quando endereço mudar (não remover, apenas reaplicar)
        // Os cupons serão reaplicados automaticamente quando o pedido for salvo/fechado
        setEndereco(endereco);
    }

    const selecionarProduto = (produto: IProdutoPedido) => {
        const produtoIndex = produtosSelecionados.findIndex(produtoProcurado => produtoProcurado.uuid == produto.uuid);

        if (produtoIndex !== -1) {
            const novosProdutos = produtosSelecionados;
            novosProdutos.splice(produtoIndex, 1);
            setProdutosSelecionados([...novosProdutos]);
            return;
        }

        setProdutosSelecionados([...produtosSelecionados, produto]);
    }

    // Função para atualizar mesas silenciosamente quando websocket recebe atualização
    const atualizarMesasSilenciosamente = async () => {
        // handleRefresh já faz a requisição sem mostrar loading
        await handleRefresh();
    };

    // Registrar callback para atualizar mesas quando websocket receber atualizações
    useEffect(() => {
        if (registrarCallbackAtualizacao && tipo === TipoPedido.mesa) {
            registrarCallbackAtualizacao(atualizarMesasSilenciosamente);

            return () => {
                if (removerCallbackAtualizacao) {
                    removerCallbackAtualizacao(atualizarMesasSilenciosamente);
                }
            };
        }
    }, [tipo, registrarCallbackAtualizacao, removerCallbackAtualizacao, atualizarMesasSilenciosamente]);

    const alterarValorFrete = (valor: number) => {
        // Reaplicar cupons quando valor do frete mudar (não remover, apenas reaplicar)
        // Os cupons serão reaplicados automaticamente quando o pedido for salvo/fechado
        
        setEndereco((enderecoAtual) => {
            if (!enderecoAtual || !enderecoAtual.regiao) {
                snackbar("Endereço indefinido", { severity: "error" });
                return enderecoAtual;
            }

            return {
                ...enderecoAtual,
                regiao: {
                    ...enderecoAtual.regiao,
                    preco: valor,
                },
            };
        });

        snackbar("Frete atualizado", { severity: "success" });
    }

    return (
        <OperacaoContext.Provider value={{
            adicionarProduto,
            adicionarProdutosCombo,
            editarProduto,
            configuracao,
            produtos,
            valorTotalProdutos,
            valorTotalPedido,
            valorDescontoTotal,
            valorTaxaServico,
            percentualTaxaServico,
            taxaServicoAplicavel,
            taxaServicoRemovida,
            alternarTaxaServico,
            alterarPagamentos,
            pagamentos,
            aumentarQuantidade,
            diminuirQuantidade,
            removerProduto,
            abrirPedido,
            loading,
            alterarTipo,
            tipo,
            pedidos,
            fecharPedido,
            valorRecebido,
            valorTroco,
            agendadoPara,
            agendarHorario,
            alterarEndereco,
            cliente,
            endereco,
            removerCliente,
            valorFrete,
            vincularCliente,
            vincularEndereco,
            alterarValorFrete,
            salvarPedido,
            resetarTudo,
            numeroTotalItens,
            numeroPedidoDiario,
            handleModalNotaFiscal,
            handleSwitchNotaFiscal,
            modalNotaFiscal,
            switchNotaFiscal,
            selecionarProduto,
            produtosSelecionados,
            fluxoContexto,
            configuracaoNFCe,
            loadingVincularEndereco,
            handleSwitchImprimirAutomatico,
            switchImprimirAutomatico,
            abrirDrawerPagamento,
            drawerPagamentoAberto,
            fecharDrawerPagamento,
            atualizarMesasSilenciosamente,
            cupons,
            aplicarCupom,
            removerCupom,
            drawerCuponsAberto,
            abrirDrawerCupons,
            fecharDrawerCupons
        }}>
            <EmitirNotaFiscalModal
                cancelar={handleModalNotaFiscal}
                concluir={fecharPedido}
                open={modalNotaFiscal}
                dadosCliente={cliente}
                loading={loading}
            />
            <DividirPagamento
                aberto={drawerPagamentoAberto}
                fechar={fecharDrawerPagamento}
                valorTotalPedido={valorTotalPedido}
                produtos={produtos}
                fecharPedido={fecharPedido}
                tipoPedido={tipo}
                valorFrete={valorFrete}
            />
            {children}
        </OperacaoContext.Provider>
    );
} 